
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { AdyenAmexServiceLevel, AdyenPaymentMethodType } from '@/enums/SmoothrPay';
import { AddVenuePaymentMethodsRequest, VenuePaymentMethod } from '@/interfaces/models/SmoothrPay';

/**
 * Payment methods requested on a venue's Adyen store. PayPal is deliberately
 * not offered — Smoothr Pay does not support it — but an already-enabled one
 * still reads back with its name so it can be removed.
 */
@Component
export default class VenuePaymentMethodsDialog extends Vue {
  @Prop({ type: Boolean, default: false }) public visible!: boolean;
  @Prop({ type: String, default: '' }) public venueName!: string;
  @Prop({ type: Array, default: () => [] }) public methods!: VenuePaymentMethod[];
  @Prop({ type: Boolean, default: false }) public loading!: boolean;
  @Prop({ type: Boolean, default: false }) public saving!: boolean;
  @Prop({ type: Boolean, default: false }) public canManage!: boolean;

  public types: AdyenPaymentMethodType[] = [];
  public applePayDomains: string = '';
  public googlePayMerchantId: string = '';
  public amexServiceLevel: AdyenAmexServiceLevel = AdyenAmexServiceLevel.NO_CONTRACT;
  public error: string = '';

  get typeItems() {
    return [
      { text: 'Visa', value: AdyenPaymentMethodType.VISA },
      { text: 'Mastercard', value: AdyenPaymentMethodType.MASTERCARD },
      { text: 'Google Pay', value: AdyenPaymentMethodType.GOOGLE_PAY },
      { text: 'Apple Pay', value: AdyenPaymentMethodType.APPLE_PAY },
      { text: 'Amex', value: AdyenPaymentMethodType.AMEX },
      { text: 'girocard', value: AdyenPaymentMethodType.GIROCARD },
    ];
  }

  get amexItems() {
    return [
      { text: this.$t('smoothrPay.paymentMethods.amex.noContract'), value: AdyenAmexServiceLevel.NO_CONTRACT },
      { text: this.$t('smoothrPay.paymentMethods.amex.gateway'), value: AdyenAmexServiceLevel.GATEWAY_CONTRACT },
      {
        text: this.$t('smoothrPay.paymentMethods.amex.paymentDesignator'),
        value: AdyenAmexServiceLevel.PAYMENT_DESIGNATOR_CONTRACT,
      },
    ];
  }

  get needsApplePay(): boolean {
    return this.types.indexOf(AdyenPaymentMethodType.APPLE_PAY) > -1;
  }

  get needsGooglePay(): boolean {
    return this.types.indexOf(AdyenPaymentMethodType.GOOGLE_PAY) > -1;
  }

  get needsAmex(): boolean {
    return this.types.indexOf(AdyenPaymentMethodType.AMEX) > -1;
  }

  @Watch('visible')
  public onVisibleChange(visible: boolean) {
    if (visible) {
      this.types = [];
      this.applePayDomains = '';
      this.googlePayMerchantId = '';
      this.error = '';
    }
  }

  public typeLabel(method: VenuePaymentMethod): string {
    const type = method.adyen && method.adyen.type;
    if (!type) {
      return '—';
    }
    const known = this.typeItems.find((item: any) => item.value === type);
    return known ? known.text : type;
  }

  public add() {
    this.error = '';
    const payload: AddVenuePaymentMethodsRequest = { types: this.types };

    if (this.needsApplePay) {
      const domains = this.applePayDomains
        .split(',')
        .map((domain: string) => domain.trim())
        .filter((domain: string) => !!domain);
      if (!domains.length) {
        this.error = this.$t('smoothrPay.paymentMethods.applePayDomainsRequired') as string;
        return;
      }
      payload.applePay = { domains };
    }

    if (this.needsGooglePay) {
      // Adyen wants 16–20 characters; a shorter id is refused server-side.
      if (this.googlePayMerchantId.length < 16 || this.googlePayMerchantId.length > 20) {
        this.error = this.$t('smoothrPay.paymentMethods.googlePayMerchantIdRequired') as string;
        return;
      }
      payload.googlePay = { merchantId: this.googlePayMerchantId };
    }

    if (this.needsAmex) {
      payload.amex = { serviceLevel: this.amexServiceLevel };
    }

    this.$emit('add', payload);
  }
}
